import { z } from 'zod';
import type { CodeBoard } from '@/types/oscar';

// Migration 060 (meeting 20/08/2026): a Site is one physical address; the rest
// are the units that operate there. Must stay in step with CK_Location_LocationType.
export const locationTypes = ['Site', 'Branch', 'ToolStore', 'Warehouse', 'Office', 'Floor', 'Other'] as const;

export const locationTypeLabels: Record<(typeof locationTypes)[number], string> = {
  Site: 'Site (one per physical address)',
  Branch: 'Branch',
  ToolStore: 'Tool store',
  Warehouse: 'Warehouse',
  Office: 'Office',
  Floor: 'Floor / level',
  Other: 'Other',
};

/**
 * What a branch code brings with it from the reporting hierarchy (oscar.BaseplanCode,
 * Locations > Regions, areas & reporting branches): its area, that area's region, the
 * operating-branch flag and whether Baseplan has it as a minor code. Null when there is
 * no code, the hierarchy is not loaded, or the code is not in it (a legacy value) - the
 * form then leaves those fields as they are, to edit by hand.
 */
export function codeDerivedFields(code: string | undefined, board: CodeBoard | undefined) {
  const c = code ? board?.Codes.find((x) => x.Code === code) : undefined;
  if (!c || !board) return null;
  const area = board.Areas.find((a) => a.AreaId === c.AreaId);
  return {
    AreaId: c.AreaId,
    RegionId: area?.RegionId ?? null,
    IsBranch: c.IsBranch,
    IsMinor: c.InGLMinor === true,
    Description: c.Description,
  };
}

/**
 * Does Australia Post deliver to the physical address (Monday's Branch Directory,
 * 25/10/2026)? Must stay in step with LocationsFunctions.AustPostValues and
 * CK_Location_AustPostDelivers.
 */
export const austPostValues = ['Yes', 'No', 'ReferAlternate', 'ConfirmFirst', 'NA'] as const;

export const austPostLabels: Record<(typeof austPostValues)[number], string> = {
  Yes: 'Yes',
  No: 'No',
  ReferAlternate: 'No - send via another branch',
  ConfirmFirst: 'Confirm before sending',
  NA: 'Not applicable (no premises)',
};

/**
 * The value a "parent location" control should hold, from whatever the form or
 * the API gave it.
 *
 * ⛔ ZERO MEANS "NO PARENT" HERE. The API's detach-from-parent signal is 0 (null
 * would mean "unchanged"), and this form sends 0 for a top-level location — so
 * 0 has to be ACCEPTED on the way in as well. It was not: the schema demanded
 * `.positive()`, so a 0 arriving in the field failed with "Number must be
 * greater than 0" against a control correctly reading "— top-level site".
 *
 * ⚠ Reported 05/10/2026: editing a top-level location showed that error on the
 * first Save and went through on the second.
 *
 * ⚠ NaN too. `Number(null)` is 0 and `Number(undefined)` is NaN, and neither is
 * a parent — both are the absence of one.
 */
export function toParentLocationId(v: unknown): number | null {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * The same rule for the Region and Area pickers. `v === '' ? null : Number(v)`
 * turned the form's initial null into 0 (Number(null) is 0), which the area
 * check then cleared and the select set back - a render loop on a new location.
 */
export const toOptionalId = toParentLocationId;

export const locationSchema = z.object({
  LocationDesc: z.string().trim().min(1, 'Location is required'),
  LocationType: z.enum(locationTypes),
  /** The PRIMARY branch - decides area and region; unique across locations. */
  BranchCode: z.string().trim().optional(),
  /** The location's other branches (team walk-through 26/10/2026: Pakenham runs two). */
  OtherBranchCodes: z.array(z.string().trim().min(1)).max(19, 'At most 20 branches at one location'),
  /** The site this location sits within. Sub-locations are one level deep. */
  ParentLocationId: z.number().int().positive().nullable().optional(),
  RegionId: z.number().int().positive().nullable().optional(),
  AreaId: z.number().int().positive().nullable().optional(),
  OU: z.string().trim().optional(),
  AdOffice: z.string().trim().optional(),
  PhysicalStreet: z.string().trim().optional(),
  PhysicalCity: z.string().trim().optional(),
  PhysicalState: z.string().trim().optional(),
  PhysicalPostcode: z.string().trim().optional(),
  PhysicalCountry: z.string().trim().optional(),
  PostalStreet: z.string().trim().optional(),
  PostOfficeBox: z.string().trim().optional(),
  PostalCity: z.string().trim().optional(),
  PostalState: z.string().trim().optional(),
  PostalPostcode: z.string().trim().optional(),
  PostalCountry: z.string().trim().optional(),
  IsBranch: z.boolean(),
  IsMinor: z.boolean(),
  IsStockLocation: z.boolean(),
  Active: z.boolean(),
  Notes: z.string().trim().optional(),
  Phone: z.string().trim().max(50, 'At most 50 characters').optional(),
  AustPostDelivers: z.union([z.enum(austPostValues), z.literal('')]),
  AddressNotes: z.string().trim().max(500, 'At most 500 characters').optional(),
});

export type LocationFormData = z.infer<typeof locationSchema>;

/**
 * The branches a location holds, as the API takes them: the primary first, then the
 * others, blanks and repeats dropped (a code picked as primary is not listed twice).
 */
export function branchCodesFor(primary: string | undefined, others: readonly string[]): string[] {
  const out: string[] = [];
  for (const raw of [primary ?? '', ...others]) {
    const c = raw.trim();
    if (c && !out.some((x) => x.toLowerCase() === c.toLowerCase())) out.push(c);
  }
  return out;
}
